import PropTypes from "prop-types";
import { User, Bot, Eye, UserCheck, Settings } from "lucide-react";
import "../../styles/admin-base.css";

export const actionTone = {
  // General / System
  "admin_login_success": "is-green",
  "admin_logout": "is-red",
  "admin_logout_success": "is-red",
  "department_login_success": "is-green",
  "department_logout": "is-red",
  "admin_created": "is-blue",
  "admin_profile_update": "is-blue",
  "settings-change": "is-orange",
  "override": "is-orange",
  
  // Users & Registrations
  "registration_approved": "is-green",
  "registration_rejected": "is-red",
  "registration_employment_updated": "is-blue",
  "user_field_update": "is-blue",
  "user_password_change": "is-green",
  
  // Events
  "event_created": "is-green",
  "event_updated": "is-blue",
  "event_deleted": "is-red",
  
  // Venues & Bookings
  "venue_created": "is-green",
  "venue_updated": "is-blue",
  "venue_deleted": "is-red",
  "approve_venue_booking": "is-green",
  "reject_venue_booking": "is-red",
  
  // ID Cards
  "id_card_status_updated": "is-blue",
  "id_card_shipping_updated": "is-blue",
  "id_card_released": "is-green",
  "id_card_rejected": "is-red",
  "id_card_notification_sent": "is-blue",
  
  // Tickets & AI
  "ticket_updated": "is-blue",
  "ticket_message_created": "is-blue",
  "ticket_department_assigned": "is-blue",
  "department_ticket_response": "is-blue",
  "department_ticket_status_updated": "is-orange",
  "auto-assign": "is-blue",
  "model-call": "is-blue",
  "view-original": "is-gray"
};

export function formatTimestamp(timestamp, compact) {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }
  if (compact) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return date.toLocaleString();
}

export function getActionDescription(entry) {
  const { action, metadata = {}, reason, ticketId, description } = entry;
  if (typeof description === "string" && description.trim()) {
    return description;
  }

  if (typeof metadata?.description === "string" && metadata.description.trim()) {
    return metadata.description;
  }

  const ticketNo = entry.ticketNo || entry.ticket_no || metadata.ticketNo || metadata.ticket_no || ticketId;

  switch (action) {
    case "admin_login_success":
      return "Logged in to the admin portal";
    case "admin_logout":
    case "admin_logout_success":
      return "Logged out of the admin portal";
    case "department_login_success":
      return "Logged in to the department portal";
    case "department_logout":
      return "Logged out of the department portal";
    case "registration_approved": {
      const registrationId = metadata.registrationId ?? entry.entityId;
      return registrationId ? `Approved membership registration #${registrationId}` : "Approved a membership registration";
    }
    case "registration_rejected": {
      const registrationId = metadata.registrationId ?? entry.entityId;
      const reasonText = reason || metadata.reason;
      return registrationId
        ? `Rejected membership registration #${registrationId}${reasonText ? ` — Reason: ${reasonText}` : ""}`
        : `Rejected a membership registration${reasonText ? ` — Reason: ${reasonText}` : ""}`;
    }
    case "auto-assign":
      return `Auto-assigned ticket #${ticketNo} to ${metadata.assignedTo || "queue"}`;
    case "override":
      return `Manually overrode ticket #${ticketNo} routing to ${metadata.assignedTo || "manual queue"}${reason ? ` — Reason: ${reason}` : ""}`;
    case "view-original":
      return `Viewed original content for ${ticketNo ? `ticket #${ticketNo}` : "a record"}`;
    case "model-call":
      return `Processed ticket #${ticketNo} using ${metadata.model || "AI model"}`;
    case "approve":
      return `Approved ticket #${ticketNo}${reason ? ` — ${reason}` : ""}`;
    case "reject":
      return `Rejected ticket #${ticketNo}${reason ? ` — Reason: ${reason}` : ""}`;
    case "settings-change":
      return `Changed setting from "${metadata.previousValue || "previous"}" to "${metadata.newValue || "new value"}"`;
    case "admin_created": {
      const roles = Array.isArray(metadata?.createdAdmin?.roles)
        ? metadata.createdAdmin.roles
          .map((role) => role?.name || role?.code)
          .filter((value) => typeof value === "string" && value.trim().length)
        : [];
      if (roles.length) {
        return `Created a new admin account with roles: ${roles.join(", ")}`;
      }
      return "Created a new admin account";
    }
    case "admin_profile_update":
      return "Updated an admin profile";
    case "user_field_update":
      return "Updated member profile fields";
    case "user_password_change":
      return "Changed a member's password";
    case "registration_employment_updated":
      return "Updated member registration employment details";
    case "event_created":
      return `Created event ${metadata.eventTitle || ""}`.trim();
    case "event_updated":
      return `Updated event ${metadata.eventTitle || ""}`.trim();
    case "event_deleted":
      return `Deleted event ${metadata.eventTitle || ""}`.trim();
    case "venue_created":
      return `Created venue ${metadata.venueName || ""}`.trim();
    case "venue_updated":
      return `Updated venue ${metadata.venueName || ""}`.trim();
    case "venue_deleted":
      return `Deleted venue ${metadata.venueName || ""}`.trim();
    case "id_card_status_updated":
      return "Updated ID card request status";
    case "id_card_shipping_updated":
      return "Updated ID card shipping status";
    case "id_card_released":
      return "Released ID card request";
    case "id_card_rejected":
      return "Rejected ID card request";
    case "id_card_notification_sent":
      return "Sent ID card notification";
    case "ticket_updated":
      return "Updated ticket details";
    case "ticket_department_assigned":
      return "Assigned ticket to a department";
    case "ticket_message_created":
      return "Posted a message on ticket";
    case "department_ticket_response":
      return "Posted a department response on ticket";
    case "department_ticket_status_updated": {
      const prev = metadata.previousStatus;
      const next = metadata.status;
      if (prev && next) {
        return `Updated ticket status from ${prev} to ${next}`;
      }
      return "Updated ticket status";
    }
    default:
      return `Performed ${action.replace(/[-_]/g, " ")} on ${ticketNo ? `ticket #${ticketNo}` : "system"}`;
  }
}

export function ActorIcon({ actor }) {
  switch (actor) {
    case "AI":
      return <Bot size={16} color="var(--admin-primary)" />;
    case "admin":
      return <UserCheck size={16} color="var(--admin-success)" />;
    case "proponent":
      return <User size={16} color="var(--admin-ai-accent)" />;
    case "department":
      return <UserCheck size={16} color="var(--admin-warning)" />;
    case "system":
    default:
      return <Settings size={16} color="var(--admin-text-muted)" />;
  }
}

ActorIcon.propTypes = {
  actor: PropTypes.string.isRequired,
};

export default function AuditRow({ entry, onViewTicket, compact = false, className = "" }) {
  const tone = actionTone[entry.action] || "";
  const description = getActionDescription(entry);

  if (compact) {
    return (
      <div className={`admin-audit-row compact ${className}`.trim()}>
        <div className="admin-audit-row__meta">
          <ActorIcon actor={entry.actor} />
          <span>{formatTimestamp(entry.timestamp, true)}</span>
        </div>
        <div className="admin-audit-row__body" title={description}>
          <strong>{entry.actorName || entry.actor}</strong> {description}
        </div>
        {entry.ticketId && onViewTicket ? (
          <button
            type="button"
            onClick={() => onViewTicket(entry.ticketId)}
            className="admin-button"
          >
            <Eye size={14} /> View
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`admin-audit-row ${className}`.trim()}>
      <div className="admin-audit-row__side">
        <ActorIcon actor={entry.actor} />
        <span className="admin-muted">{formatTimestamp(entry.timestamp)}</span>
        <span className={`admin-chip ${tone}`.trim()}>{entry.action.replace(/[-_]/g, " ")}</span>
      </div>
      <div className="admin-audit-row__body">
        <p>
          <strong>{entry.actorName || entry.actor}</strong> {description}
        </p>
        {entry.metadata && Object.keys(entry.metadata).length ? (
          <div className="admin-pill-group">
            {entry.metadata.model ? (
              <span className="admin-pill">Model: {entry.metadata.model}</span>
            ) : null}
            {entry.metadata.confidence ? (
              <span className="admin-pill">Confidence: {(entry.metadata.confidence * 100).toFixed(0)}%</span>
            ) : null}
          </div>
        ) : null}
      </div>
      {entry.ticketId && onViewTicket ? (
        <button
          type="button"
          onClick={() => onViewTicket(entry.ticketId)}
          className="admin-button"
        >
          <Eye size={14} /> View Ticket
        </button>
      ) : null}
    </div>
  );
}

AuditRow.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    timestamp: PropTypes.string.isRequired,
    actor: PropTypes.oneOf(["AI", "admin", "department", "proponent", "system"]).isRequired,
    actorName: PropTypes.string,
    action: PropTypes.string.isRequired,
    ticketId: PropTypes.string,
    reason: PropTypes.string,
    metadata: PropTypes.object,
  }).isRequired,
  onViewTicket: PropTypes.func,
  compact: PropTypes.bool,
  className: PropTypes.string,
};
